import useBillingNotificationEntity from 'hooks/useBillingNotificationEntity';
import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { formatBillingDate } from 'util/organizationBillingFormat';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import { BillingEmptyRow } from './components/OrganizationBillingLayout';
import {
  useGetOrganizationHostedAccountsQuery,
  useGetOrganizationPricingPreviewQuery,
  usePreviewOrganizationHostedDeletionMutation,
  useRequestOrganizationHostedDeletionMutation
} from 'rtk/org-admin/organizationBilling.slice';

const DEFAULT_PAGE_SIZE = 10;

const dataOf = value => value?.Data ?? value?.data ?? value;
const field = (value, name) =>
  value?.[name] ?? value?.[name[0].toLowerCase() + name.slice(1)];
const requestKey = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const errorText = error =>
  error?.data?.Data ??
  error?.data?.data ??
  error?.Data ??
  error?.Message ??
  error?.error ??
  'The request could not be completed.';
const money = cents => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';
  return `$${(amount / 100).toFixed(2)}`;
};
const sourceBadge = source => {
  if (source === 'Custom') return { bg: 'info', label: 'Custom' };
  if (source === 'Standard') return { bg: 'primary', label: 'Standard' };
  if (source === 'RequiresPricingReview')
    return { bg: 'warning', label: 'Needs review' };
  if (source === 'NotApplicable')
    return { bg: 'secondary', label: 'Not applicable' };
  return { bg: 'secondary', label: source || '—' };
};
const sharingBadge = status => {
  if (status === 'Enabled') return { bg: 'success', label: 'Enabled' };
  if (status === 'Disabled') return { bg: 'secondary', label: 'Disabled' };
  return { bg: 'secondary', label: status || '—' };
};
const connectionBadge = status => {
  if (status === 'Connected') return { bg: 'success', label: 'Connected' };
  if (status === 'Disconnected')
    return { bg: 'warning', label: 'Disconnected' };
  return { bg: 'secondary', label: status || '—' };
};
const retentionBadge = state => {
  if (state === 'AccessRevoked')
    return { bg: 'danger', label: 'Access revoked' };
  if (state === 'Deleted' || state === 'Purged')
    return { bg: 'danger', label: 'Data deleted' };
  return { bg: 'secondary', label: state || '—' };
};
const cycleBadge = account => {
  if (field(account, 'BillableThisCycle'))
    return { bg: 'success', label: 'Counted' };
  const explanation = String(field(account, 'Explanation') || '').toLowerCase();
  if (explanation.includes('deferred'))
    return { bg: 'warning', label: 'Deferred' };
  return { bg: 'secondary', label: 'Not counted' };
};

const AdminHostedAccounts = () => {
  const linked = useBillingNotificationEntity('hosted-account');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [deletionQuote, setDeletionQuote] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const accountsQuery = useGetOrganizationHostedAccountsQuery(
    {
      indexStart: pageIndex * pageSize,
      maxResults: pageSize,
      search: searchTerm
    },
    { refetchOnMountOrArgChange: true }
  );
  const previewQuery = useGetOrganizationPricingPreviewQuery(undefined, {
    refetchOnMountOrArgChange: true
  });
  const [previewDeletion, previewDeletionState] =
    usePreviewOrganizationHostedDeletionMutation();
  const [requestDeletion, requestDeletionState] =
    useRequestOrganizationHostedDeletionMutation();
  const accountsPayload = dataOf(accountsQuery.data);
  const loadedAccounts = field(accountsPayload, 'Objects') ?? [];
  const accounts = linked.entity ? [linked.entity] : loadedAccounts;
  const totalRecords = linked.entity
    ? 1
    : field(accountsPayload, 'TotalRecords') ?? accounts.length;
  const preview = dataOf(previewQuery.data);
  const lines = (field(preview, 'Lines') ?? []).filter(
    line => field(line, 'ProductCode') === 'HostedData'
  );
  const needsReview = lines.some(line => field(line, 'RequiresPricingReview'));
  const accountsLoading =
    accountsQuery.isLoading ||
    accountsQuery.isFetching ||
    searchInput.trim() !== searchTerm;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPageIndex(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openDeletion = async account => {
    setDeleting(account);
    setDeletionQuote(null);
    setConfirmation('');
    try {
      const result = await previewDeletion(field(account, 'GUID')).unwrap();
      setDeletionQuote(dataOf(result));
    } catch (error) {
      toast.error(errorText(error));
      setDeleting(null);
    }
  };

  const confirmDeletion = async () => {
    try {
      await requestDeletion({
        guid: field(deleting, 'GUID'),
        idempotencyKey: requestKey()
      }).unwrap();
      toast.success('Hosted-data deletion is scheduled.');
      setDeleting(null);
      setDeletionQuote(null);
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  return (
    <Row className="mb-3 g-3">
      {linked.targetGuid && (
        <Alert variant={linked.error ? 'danger' : 'info'}>
          {linked.error ||
            (linked.loading
              ? 'Loading the affected hosted account...'
              : 'Showing the affected hosted account.')}{' '}
          <Link to="/organization/billing/hosted-accounts">
            Show all hosted accounts
          </Link>
        </Alert>
      )}

      <Col lg={12}>
        <FalconComponentCard>
          <FalconComponentCard.Header
            title="Current cycle price"
            subtitle="Hosted-data price for the notaries counted in this cycle."
            onRefresh={() => previewQuery.refetch()}
            loading={previewQuery.isFetching}
          />
          <FalconComponentCard.Body>
            {previewQuery.isLoading && (
              <Flex justifyContent="center" className="py-3">
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading price</span>
                </Spinner>
              </Flex>
            )}
            {previewQuery.isError && (
              <Alert variant="warning" className="mb-0">
                {errorText(previewQuery.error)}
              </Alert>
            )}
            {!previewQuery.isLoading &&
              !previewQuery.isError &&
              needsReview && (
                <Alert variant="warning">
                  A price is not configured for this quantity. Collection stays
                  on hold until pricing review.
                </Alert>
              )}
            {!previewQuery.isLoading &&
              !previewQuery.isError &&
              lines.length === 0 && (
                <p className="mb-0 text-600 fs--1">
                  There is no billable hosted-data quantity for this cycle.
                </p>
              )}
            {lines.length > 0 && (
              <Row className="g-3">
                {lines.map(line => {
                  const source = sourceBadge(field(line, 'Source'));
                  const quantity = Number(field(line, 'Quantity')) || 0;
                  return (
                    <Col
                      md={6}
                      xl={4}
                      key={`${field(line, 'ProductCode')}-${field(
                        line,
                        'Frequency'
                      )}-${field(line, 'Reason')}`}
                    >
                      <Card body className="shadow-none border h-100">
                        <Flex
                          justifyContent="between"
                          alignItems="center"
                          className="mb-3"
                        >
                          <div>
                            <h6 className="mb-0">Hosted data</h6>
                            <p className="fs--1 text-600 mb-0">
                              {field(line, 'Frequency')}
                            </p>
                          </div>
                          <SoftBadge bg={source.bg} pill>
                            {source.label}
                          </SoftBadge>
                        </Flex>
                        <Flex justifyContent="between" className="fs--1 mb-2">
                          <span className="text-600">Hosted accounts</span>
                          <span className="fw-semi-bold">{quantity}</span>
                        </Flex>
                        <Flex justifyContent="between" className="fs--1 mb-2">
                          <span className="text-600">Price per account</span>
                          <span className="fw-semi-bold">
                            {money(field(line, 'UnitAmountCents')) || '—'}
                          </span>
                        </Flex>
                        <hr className="my-2" />
                        <Flex justifyContent="between" alignItems="baseline">
                          <span className="text-700">Cycle total</span>
                          <span className="fs-2 fw-bold text-primary mb-0">
                            {money(field(line, 'AmountCents')) || '—'}
                          </span>
                        </Flex>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </FalconComponentCard.Body>
        </FalconComponentCard>
      </Col>

      <Col lg={12}>
        <Card className="overflow-hidden">
          <Card.Header className="bg-light">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-1">Hosted accounts</h5>
                <p className="fs--1 text-600 mb-0">
                  Notaries whose journal data is hosted for this organization.
                </p>
              </div>
              <InputGroup className="w-auto">
                <Form.Control
                  type="search"
                  value={searchInput}
                  onChange={event => setSearchInput(event.target.value)}
                  placeholder="Search notaries..."
                  aria-label="Search hosted accounts"
                />
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
              </InputGroup>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {accountsQuery.isLoading && (
              <Flex justifyContent="center" className="py-4">
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading accounts</span>
                </Spinner>
              </Flex>
            )}
            {accountsQuery.isError && (
              <Alert variant="warning" className="m-3 mb-0">
                {errorText(accountsQuery.error)}
              </Alert>
            )}
            {!accountsQuery.isLoading && (
              <div className="px-3 py-2">
                <Pagination
                  className="py-0"
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={accounts.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={size => {
                    setPageSize(size);
                    setPageIndex(0);
                  }}
                  endOfResults={
                    linked.entity ||
                    (field(accountsPayload, 'EndOfResults') ?? true)
                  }
                  disabled={accountsLoading}
                  pageSizeOptions={[5, 10, 15, 25]}
                  onRefresh={() => accountsQuery.refetch()}
                  loading={accountsLoading}
                />
              </div>
            )}
            <Table
              responsive
              className={`table-dashboard fs--1 mb-0${
                accountsLoading ? ' refreshing-skeleton' : ''
              }`}
            >
              <thead className="bg-200 text-900">
                <tr>
                  <th scope="col">Notary</th>
                  <th scope="col" className="text-nowrap">
                    Sharing
                  </th>
                  <th scope="col" className="text-nowrap">
                    Connection
                  </th>
                  <th scope="col" className="text-nowrap">
                    Rate
                  </th>
                  <th scope="col" className="text-nowrap">
                    First hosted
                  </th>
                  <th scope="col" className="text-nowrap">
                    This cycle
                  </th>
                  <th scope="col" className="text-nowrap">
                    Data retention
                  </th>
                </tr>
              </thead>
              <tbody>
                {!accountsQuery.isLoading &&
                  !accountsQuery.isError &&
                  !accounts.length && (
                    <BillingEmptyRow colSpan={7}>
                      {searchTerm
                        ? 'No notaries match this search.'
                        : 'No notary data is hosted for this organization yet.'}
                    </BillingEmptyRow>
                  )}
                {accounts.map((account, index) => {
                  const sharing = sharingBadge(field(account, 'SharingStatus'));
                  const connection = connectionBadge(
                    ['Deleted', 'Purged'].includes(field(account, 'State'))
                      ? 'Disconnected'
                      : field(account, 'ConnectionStatus')
                  );
                  const cycle = cycleBadge(account);
                  const eligible = Boolean(
                    field(account, 'EligibleForGroupDiscount')
                  );
                  const firstHosted = field(account, 'FirstHostedUtc');
                  const isLast = index === accounts.length - 1;
                  return (
                    <tr
                      key={field(account, 'GUID')}
                      className={
                        linked.targetGuid === field(account, 'GUID')
                          ? 'align-middle table-info'
                          : 'align-middle'
                      }
                    >
                      <td
                        className={
                          isLast ? 'border-0 fw-semi-bold' : 'fw-semi-bold'
                        }
                      >
                        {field(account, 'NotaryName') || '—'}
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        <SoftBadge bg={sharing.bg} pill>
                          {sharing.label}
                        </SoftBadge>
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        <SoftBadge bg={connection.bg} pill>
                          {connection.label}
                        </SoftBadge>
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        <SoftBadge bg={eligible ? 'primary' : 'warning'} pill>
                          {eligible ? 'Standard rate' : 'Does not count'}
                        </SoftBadge>
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        {firstHosted
                          ? formatBillingDate(firstHosted, 'll')
                          : '—'}
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        <SoftBadge bg={cycle.bg} pill>
                          {cycle.label}
                        </SoftBadge>
                      </td>
                      <td className={`text-nowrap${isLast ? ' border-0' : ''}`}>
                        {field(account, 'State') === 'DeletionPending' ? (
                          <div>
                            <SoftBadge bg="warning" pill>
                              Deletion pending
                            </SoftBadge>
                            <div className="text-600 fs--2 mt-1">
                              Access ends{' '}
                              {formatBillingDate(
                                field(account, 'DeletionEffectiveUtc'),
                                'll'
                              )}
                            </div>
                          </div>
                        ) : field(account, 'State') === 'Active' ? (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="text-nowrap px-3"
                            onClick={() => openDeletion(account)}
                          >
                            <FontAwesomeIcon
                              icon={faTrashAlt}
                              className="me-1"
                            />
                            Request deletion
                          </Button>
                        ) : (
                          (() => {
                            const retention = retentionBadge(
                              field(account, 'State')
                            );
                            return (
                              <SoftBadge bg={retention.bg} pill>
                                {retention.label}
                              </SoftBadge>
                            );
                          })()
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
      <Modal show={!!deleting} onHide={() => setDeleting(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request hosted-data deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewDeletionState.isLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {deletionQuote && (
            <>
              <Alert variant="warning">
                Data for {field(deleting, 'NotaryName') || 'this notary'}{' '}
                remains accessible and billable through{' '}
                <strong>
                  {formatBillingDate(
                    field(deletionQuote, 'EffectiveUtc'),
                    'lll'
                  )}
                </strong>
                . Access ends at that time. Data deletion is processed
                separately.
              </Alert>
              <Form.Group controlId="hosted-deletion-confirmation">
                <Form.Label>Type DELETE to confirm</Form.Label>
                <Form.Control
                  value={confirmation}
                  onChange={event => setConfirmation(event.target.value)}
                  autoComplete="off"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={
              confirmation !== 'DELETE' || requestDeletionState.isLoading
            }
            onClick={confirmDeletion}
          >
            Schedule deletion
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default AdminHostedAccounts;
